import { useId } from 'react';

/**
 * One id, four derivatives — so a control and its `ChoiceField` always agree about
 * what the error node is called.
 *
 * The precedence here is the same one `ChoiceField` renders, and it has to be: only
 * one message node is ever in the DOM, so `describedBy` names only that one.
 * Pointing `aria-describedby` at two ids when one element does not exist is the
 * kind of silent mismatch that survives every test and confuses a screen reader.
 */
export interface FieldMessages {
  error?: string | null;
  success?: string | null;
  hint?: string | null;
}

export interface FieldIds {
  /** The control's id — the same value that must be handed to `ChoiceField`. */
  id: string;
  /** The id of the single message node that is actually rendered, or `undefined`. */
  describedBy: string | undefined;
  errorId: string;
  successId: string;
  hintId: string;
  /** True when the control must carry `aria-invalid="true"`. */
  invalid: boolean;
}

export function useFieldIds(providedId: string | undefined, messages: FieldMessages): FieldIds {
  const generated = useId();
  const id = providedId ?? generated;

  const errorId = `${id}-error`;
  const successId = `${id}-success`;
  const hintId = `${id}-hint`;

  const describedBy = messages.error ? errorId : messages.success ? successId : messages.hint ? hintId : undefined;

  return { id, describedBy, errorId, successId, hintId, invalid: Boolean(messages.error) };
}
